import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  Clock,
  ArrowRight,
  Copy,
  Loader2,
  Sparkles,
} from "lucide-react";
import { BlogNav, BlogFooter } from "./BlogListPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Renderiza um subset simples de markdown: h1/h2, negrito, itálico, listas e parágrafos. */
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];

  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1 text-slate-300 my-4">
          {listBuf.map((li, i) => (
            <li
              key={`li-${out.length}-${i}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inline(li)) }}
            />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };

  const inline = (s) =>
    s
      .replace(/`([^`]+)`/g, '<code class="bg-slate-800 px-1.5 py-0.5 rounded text-[#D4AF37] text-xs font-mono">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith("# ")) {
      flushList();
      out.push(
        <h1 key={idx} className="text-3xl sm:text-4xl font-serif font-bold text-white mt-2 mb-6 leading-tight">
          {line.slice(2)}
        </h1>
      );
    } else if (line.startsWith("## ")) {
      flushList();
      out.push(
        <h2 key={idx} className="text-2xl font-serif font-bold text-[#D4AF37] mt-10 mb-4 leading-tight">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith("- ")) {
      listBuf.push(line.slice(2));
    } else {
      flushList();
      out.push(
        <p
          key={idx}
          className="text-slate-300 leading-[1.85] my-4 text-[15px]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inline(line)) }}
        />
      );
    }
  });
  flushList();
  return out;
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setData(res.data);
        document.title = `${res.data.post.title} — Lojas Integradas`;
        window.scrollTo(0, 0);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#080C14] text-white">
        <BlogNav />
        <div className="max-w-lg mx-auto text-center py-32 px-6">
          <div className="text-7xl">📖</div>
          <h1 className="mt-6 text-3xl font-serif font-bold">Post não encontrado</h1>
        {notFound || !data ? (
          <button
            data-testid="post-back-link"
            onClick={() => navigate("/blog")}
            className="mt-8 px-6 py-3 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold rounded-xl text-sm uppercase tracking-wider inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar ao Blog
          </button>
        ) : null}
        </div>
      </div>
    );
  }

  const { post, related } = data;

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] font-sans">
      <BlogNav />

      {/* Article */}
      <article className="relative z-10 max-w-3xl mx-auto px-6 pt-16 pb-24">
        <button
          data-testid="post-back-link"
          onClick={() => navigate("/blog")}
          className="text-xs uppercase tracking-widest text-slate-500 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2 mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Todos os posts
        </button>

        <div className="text-8xl mb-6 text-center">{post.cover_emoji}</div>

        <div className="flex flex-wrap gap-2 mb-4 justify-center">
          {(post.tags || []).map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded"
            >
              {t}
            </span>
          ))}
        </div>

        <h1 data-testid="post-title" className="text-4xl sm:text-5xl font-serif font-bold text-white text-center leading-[1.15]">
          {post.title}
        </h1>

        <div className="mt-6 flex items-center justify-center gap-5 text-xs text-slate-500 pb-8 border-b border-slate-800">
          <span>{post.author}</span>
          <span>·</span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {post.read_min} min de leitura
          </span>
          <span>·</span>
          <button
            data-testid="post-copy-link"
            onClick={copyLink}
            className="hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
          >
            <Copy className="w-3 h-3" /> Copiar link
          </button>
        </div>

        <div data-testid="post-content" className="mt-10 blog-body">
          {renderMarkdown(post.content_md)}
        </div>

        {/* Inline CTA */}
        <div className="mt-14 rounded-2xl bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 p-8 text-center">
          <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto" />
          <h3 className="mt-4 text-2xl font-serif font-bold text-white">
            Aplique isto na sua Loja hoje
          </h3>
          <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
            O Lojas Integradas implementa cada uma dessas boas práticas por padrão.
          </p>
          <button
            data-testid="post-inline-cta"
            onClick={() => navigate("/signup")}
            className="mt-6 px-8 py-3 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold uppercase tracking-wider text-xs rounded-xl inline-flex items-center gap-2"
          >
            Cadastrar Loja grátis <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </article>

      {/* Related */}
      {related?.length > 0 && (
        <section className="relative z-10 border-t border-slate-800/60 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                Continue lendo
              </div>
              <h2 className="mt-2 text-2xl font-serif font-bold text-white">Posts relacionados</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group rounded-2xl bg-[#0d1420]/70 border border-slate-800 hover:border-[#D4AF37]/40 p-5 hover:-translate-y-0.5 transition-all"
                >
                  <div className="text-4xl mb-4">{p.cover_emoji}</div>
                  <h3 className="text-base font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors leading-snug">
                    {p.title}
                  </h3>
                  <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> {p.read_min} min
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BlogFooter />
    </div>
  );
};

export default BlogPostPage;
